import { useEffect, useRef } from 'react';
import { AutoPreviewContextValue, AutoPreviewRegistration } from '@/types/auto-preview';

// A rootMargin that collapses the viewport to a thin, wide strip across its centre: thin top/bottom
// so only the row crossing centre qualifies, wide left/right so the strip always spans a card and
// never falls into a between-card gap. selectActive then picks the nearest intersecting card — i.e.
// the card at the viewport centre — with no scroll/resize listeners and no getBoundingClientRect.
const CENTRE_BOX = '-49% -25% -49% -25%';
export function useAutoPreviewObserver(): AutoPreviewContextValue {
  const registrationsRef = useRef<Map<Element, AutoPreviewRegistration>>(new Map());
  const intersectingRef = useRef<Map<Element, DOMRectReadOnly>>(new Map());
  const activeRef = useRef<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const setActive = (next: Element | null) => {
    const current = activeRef.current;
    if (next === current) return;
    if (current) registrationsRef.current.get(current)?.onDeactivate();
    if (next) registrationsRef.current.get(next)?.onActivate();
    activeRef.current = next;
  };
  const selectActive = () => {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    let next: Element | null = null;
    let best = Infinity;
    for (const [element, rect] of intersectingRef.current) {
      if (!registrationsRef.current.has(element)) continue;
      const dx = rect.left + rect.width / 2 - cx;
      const dy = rect.top + rect.height / 2 - cy;
      const dist = dx * dx + dy * dy;
      if (dist < best) {
        best = dist;
        next = element;
      }
    }
    setActive(next);
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) intersectingRef.current.set(entry.target, entry.boundingClientRect);else intersectingRef.current.delete(entry.target);
      }
      selectActive();
    }, {
      rootMargin: CENTRE_BOX
    });
    observerRef.current = observer;
    // Children register (via observe) before this parent effect runs, so pick them up here.
    for (const element of registrationsRef.current.keys()) observer.observe(element);
    return () => {
      observer.disconnect();
      observerRef.current = null;
      intersectingRef.current.clear();
      setActive(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the observer is created once; selectActive is a stable, ref-only closure
  }, []);
  const observe = (element: Element, onActivate: () => void, onDeactivate: () => void) => {
    registrationsRef.current.set(element, {
      onActivate,
      onDeactivate
    });
    observerRef.current?.observe(element);
    return () => {
      registrationsRef.current.delete(element);
      intersectingRef.current.delete(element);
      observerRef.current?.unobserve(element);
      if (activeRef.current === element) {
        activeRef.current = null;
        selectActive();
      }
    };
  };
  return {
    observe
  };
}